import React, { useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ToastContainer from './ToastContainer';
import { fetchSystemName } from '../api/systemSettings';
import { resolveApiUrl } from '../api/http';

const Layout = () => {
  const [hrMenuOpen, setHrMenuOpen] = useState(true);
  const [archivesMenuOpen, setArchivesMenuOpen] = useState(true); // 新增状态
  const [dailyMenuOpen, setDailyMenuOpen] = useState(true); // 日常管理
  const [trainingMenuOpen, setTrainingMenuOpen] = useState(true); // 教育培训
  const [behaviorMenuOpen, setBehaviorMenuOpen] = useState(true); // 行为管控
  const [managementMenuOpen, setManagementMenuOpen] = useState(true); // 系统管理
  const [hazardMenuOpen, setHazardMenuOpen] = useState(true); // 隐患排查(重新分组)
  const [systemName, setSystemName] = useState('众邦紫铜 安全管理系统');
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const auth = useAuth();

  useEffect(() => {
    fetchSystemName().then(setSystemName);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleMediaChange = (event) => {
      setIsMobile(event.matches);
      setIsSidebarOpen(!event.matches);
    };
    setIsMobile(mediaQuery.matches);
    setIsSidebarOpen(!mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
      return () => mediaQuery.removeEventListener('change', handleMediaChange);
    }
    mediaQuery.addListener(handleMediaChange);
    return () => mediaQuery.removeListener(handleMediaChange);
  }, []);

  const navLinkStyle = {
    display: 'block',
    width: 'fit-content',
    margin: '8px 0',
    padding: '12px 20px',
    fontSize: '16px',
    color: 'var(--text-primary)',
    background: 'var(--bg-secondary)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    textDecoration: 'none',
    transition: 'background 0.2s, color 0.2s',
  };

  const activeNavLinkStyle = {
    background: 'var(--primary-color)',
    color: '#fff',
    fontWeight: '600',
  };
  
  const subNavLinkStyle = {
    ...navLinkStyle,
    background: 'var(--bg-tertiary)',
    paddingLeft: '40px',
    fontSize: '15px',
  };

  const getNavLinkStyle = ({ isActive }) => 
    isActive ? { ...navLinkStyle, ...activeNavLinkStyle } : navLinkStyle;

  const getSubNavLinkStyle = ({ isActive }) =>
    isActive ? { ...subNavLinkStyle, ...activeNavLinkStyle } : subNavLinkStyle;

  const canViewAccessCards = auth.hasPermission(['view_access_cards']) || auth.hasPermission(['manage_access_cards']);
  // 保险管理（六险/批次等）：使用独立权限，避免 view_employees 过宽导致菜单误显示
  const canViewContributions = auth.hasPermission(['contributions_view']) || auth.hasPermission(['contributions_manage']) || auth.hasPermission(['view_employees']);
  const canViewEmployeeInsurance = auth.hasPermission(['view_employees']) || auth.hasPermission(['contributions_view']) || auth.hasPermission(['contributions_manage']);
  const canViewFeatureEntry = auth.hasPermission(['manage_education_training']) || auth.hasPermission(['special_monitoring_task_manage']) || auth.hasPermission(['special_monitoring_record_manage']) || auth.hasPermission(['special_monitoring_view_all']);
  const canViewWorkNotes = auth.hasPermission(['view_work_notes']) || auth.hasPermission(['manage_own_work_notes']) || auth.hasPermission(['manage_work_notes']);
  const canViewTempWorkers = auth.hasPermission(['view_temp_worker_usage']) || auth.hasPermission(['manage_temp_worker_usage']);
  const canViewDashboard = auth.hasPermission(['dashboard_view']) || auth.hasPermission(['view_dashboard']);
  const canViewHrData = [
    'hr_data_attendance_view',
    'hr_data_attendance_import',
    'hr_data_financial_view',
    'hr_data_financial_manage'
  ].some(perm => auth.hasPermission([perm]));
  const canViewTrainingEntrust = auth.hasPermission(['manage_trainings']) || auth.hasPermission(['manage_education_training']) || auth.hasPermission(['view_education_training']);
  const canViewWorkPlans = auth.hasPermission(['work_plans_view']) || auth.hasPermission(['work_plans_view_all']) || auth.hasPermission(['work_plans_manage']);
  const canViewLeaveOvertime = ['leave_application_view','leave_application_submit','leave_application_create','overtime_application_view','overtime_application_submit','overtime_application_create','leave_overtime_approval','leave_application_approve','overtime_application_approve','leave_overtime_ledger_view','leave_overtime_data_analysis']
    .some(perm => auth.hasPermission([perm]));

  const canViewFeedbackAdmin = auth.hasPermission(['project_manager']);
  const currentToken = window.localStorage.getItem('token') || '';
  const pdfViewerEntryUrl = resolveApiUrl(currentToken
    ? `/api/pdf-viewer?token=${encodeURIComponent(currentToken)}`
    : '/api/pdf-viewer');

  const canViewHrMenu = (
    auth.hasPermission(['view_employees']) ||
    auth.hasPermission(['employee_changes_view']) ||
    auth.hasPermission(['employee_changes_admin']) ||
    auth.hasPermission(['manage_hr_changes']) ||
    auth.hasPermission(['hr_notifications_view']) ||
    auth.hasPermission(['hr_notifications_manage']) ||
    auth.hasPermission(['hr_notifications_publish'])
  );

  const canViewQuickShotPage =
    auth.hasPermission(['hazard_quick_shot_view']) ||
    auth.hasPermission(['hazard_quick_shot_manage']) ||
    auth.hasPermission(['hazard_view']) ||
    auth.hasPermission(['hazard_manage']);

  const canViewHazardMenu =
    auth.hasPermission(['hazard_view']) ||
    auth.hasPermission(['hazard_manage']) ||
    auth.hasPermission(['hazard_quick_shot_view']) ||
    auth.hasPermission(['hazard_quick_shot_manage']);

  // 隐患排查 - 细化到页面/Tab 的权限（当用户具备任一细化权限时启用过滤；否则保持旧行为）
  const hazardGranularPerms = [
    'hazard_risk_identification_methods',
    'hazard_risk_identification_mine',
    'hazard_risk_identification_summary',
    'hazard_risk_identification_categories',
    'hazard_safety_inspections_dashboard',
    'hazard_safety_inspections_tasks',
    'hazard_safety_inspections_mine',
  ];
  const hasAnyHazardGranularPerm = hazardGranularPerms.some((p) => auth.hasPermission([p]));

  const canViewRiskIdentificationPage = !hasAnyHazardGranularPerm
    ? true
    : [
        'hazard_risk_identification_methods',
        'hazard_risk_identification_mine',
        'hazard_risk_identification_summary',
        'hazard_risk_identification_categories',
      ].some((p) => auth.hasPermission([p]));

  const canViewSafetyInspectionPage = !hasAnyHazardGranularPerm
    ? true
    : ['hazard_safety_inspections_dashboard', 'hazard_safety_inspections_tasks', 'hazard_safety_inspections_mine'].some((p) =>
        auth.hasPermission([p])
      );

  const canViewDailyMenu = (
    canViewAccessCards ||
    canViewContributions ||
    auth.hasPermission(['duty_task_manage']) ||
    auth.hasPermission(['duty_task_record_manage']) ||
    auth.hasPermission(['duty_task_view_all']) ||
    canViewWorkPlans ||
    canViewHrData ||
    auth.hasPermission(['safety_points_view']) ||
    auth.hasPermission(['safety_points_manage']) ||
    auth.hasPermission(['dorm_view']) ||
    auth.hasPermission(['dorm_manage']) ||
    auth.hasPermission(['reimbursement_view']) ||
    auth.hasPermission(['reimbursement_manage']) ||
    auth.hasPermission(['manage_archives','view_employees']) ||
    canViewFeatureEntry ||
    canViewLeaveOvertime
  );




  return (
    <>
      <ToastContainer />
      <div className="app-layout">
        {/* Left Sidebar */}
        {isMobile && isSidebarOpen && (
          <div className="app-sidebar-backdrop" onClick={() => setIsSidebarOpen(false)} />
        )}
        <div
          className={`app-sidebar ${isSidebarOpen ? 'is-open' : 'is-closed'}`}
        >
          <h2 style={{ color: 'var(--brand-primary)', textAlign: 'center', marginBottom: 32, fontSize: '24px' }}>{systemName}</h2>
          <nav
            style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
            onClick={(event) => {
              if (!isMobile) return;
              if (event.target && event.target.tagName === 'A') {
                setIsSidebarOpen(false);
              }
            }}
          >
            {canViewDashboard && <NavLink to="/dashboard" style={getNavLinkStyle}>数据看板</NavLink>}
            
            {canViewHrMenu && (
              <>
                <button onClick={() => setHrMenuOpen(!hrMenuOpen)} style={{...navLinkStyle, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>人事管理</span>
                  <span>{hrMenuOpen ? '−' : '+'}</span>
                </button>
                {hrMenuOpen && (
                  <div style={{paddingLeft: '10px'}}>
                    {auth.hasPermission(['view_employees']) && (
                      <NavLink to="/employees" end style={getSubNavLinkStyle}>人员列表</NavLink>
                    )}
                    {(
                      auth.hasPermission(['employee_changes_view']) ||
                      auth.hasPermission(['employee_changes_admin']) ||
                      auth.hasPermission(['manage_hr_changes'])
                    ) && (
                      <>
                        <NavLink to="/employee-changes" style={getSubNavLinkStyle}>人事变更</NavLink>
                        <NavLink to="/employee-onboarding" style={getSubNavLinkStyle}>流程看板</NavLink>
                      </>
                    )}
                    {(
                      auth.hasPermission(['hr_notifications_view']) ||
                      auth.hasPermission(['hr_notifications_manage']) ||
                      auth.hasPermission(['hr_notifications_publish'])
                    ) && (
                      <NavLink to="/hr-notifications" style={getSubNavLinkStyle}>通知提醒</NavLink>
                    )}
                  </div>
                )}
              </>
            )}

            {(auth.hasPermission(['manage_trainings']) || auth.hasPermission(['manage_education_training']) || auth.hasPermission(['view_education_training']) || auth.hasPermission(['view_my_training_records'])) && (
              <>
                <button onClick={() => setTrainingMenuOpen(!trainingMenuOpen)} style={{...navLinkStyle, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>教育培训</span>
                  <span>{trainingMenuOpen ? '−' : '+'}</span>
                </button>
                {trainingMenuOpen && (
                  <div style={{paddingLeft: '10px'}}>
                    {auth.hasPermission(['manage_trainings']) && (
                      <NavLink to="/education-training/dashboard" end style={getSubNavLinkStyle}>培训看板</NavLink>
                    )}
                    {(auth.hasPermission(['manage_trainings']) || auth.hasPermission(['manage_education_training'])) && (
                      <NavLink to="/education-training/ledger" end style={getSubNavLinkStyle}>培训台账</NavLink>
                    )}
                    {(auth.hasPermission(['view_my_training_records']) || auth.hasPermission(['view_education_training']) || auth.hasPermission(['manage_education_training']) || auth.hasPermission(['manage_trainings'])) && (
                      <NavLink to="/education-training/records" end style={getSubNavLinkStyle}>培训记录</NavLink>
                    )}
                    {canViewTrainingEntrust && (
                      <NavLink to="/employees/training-entrust" style={getSubNavLinkStyle}>培训委托</NavLink>
                    )}
                  </div>
                )}
              </>
            )}
            {(auth.hasPermission(['exam_view']) || auth.hasPermission(['exam_attempt']) || auth.hasPermission(['exam_manage'])) && (
              <NavLink to="/exam-system" style={getNavLinkStyle}>考试系统</NavLink>
            )}
            
            {auth.hasPermission(['manage_archives']) && (
              <>
                <button onClick={() => setArchivesMenuOpen(!archivesMenuOpen)} style={{...navLinkStyle, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>员工档案</span>
                  <span>{archivesMenuOpen ? '−' : '+'}</span>
                </button>
                {archivesMenuOpen && (
                  <div style={{paddingLeft: '10px'}}>
                    <NavLink to="/archives" style={getSubNavLinkStyle}>档案概览</NavLink>
                    <NavLink to="/employee-files" style={getSubNavLinkStyle}>安全档案</NavLink>
                  </div>
                )}
              </>
            )}

            {/* 系统管理相关入口整体下移为顶级导航，见需求 */}

            {/* 隐患管理模块（上移到日常管理之前） */}
            {canViewHazardMenu && (
              <>
                <button onClick={() => setHazardMenuOpen(!hazardMenuOpen)} style={{...navLinkStyle, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>隐患管理</span>
                  <span>{hazardMenuOpen ? '−' : '+'}</span>
                </button>
                {hazardMenuOpen && (
                  <div style={{paddingLeft: '10px'}}>
                    <NavLink to="/hazard/dorm" style={getSubNavLinkStyle}>隐患清单</NavLink>
                    {canViewQuickShotPage && (
                      <NavLink to="/hazard/quick-shot" style={getSubNavLinkStyle}>随手拍</NavLink>
                    )}
                    {canViewSafetyInspectionPage && (
                      <NavLink to="/hazard/safety-inspection" style={getSubNavLinkStyle}>安全巡查</NavLink>
                    )}
                    {canViewRiskIdentificationPage && (
                      <NavLink to="/hazard/risk-identification" style={getSubNavLinkStyle}>危险源识别</NavLink>
                    )}
                    <NavLink to="/hazard/risk-classification" style={getSubNavLinkStyle}>风险分级</NavLink>
                  </div>
                )}
              </>
            )}

            {canViewDailyMenu ? (
              <>
                <button onClick={() => setDailyMenuOpen(!dailyMenuOpen)} style={{...navLinkStyle, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>日常管理</span>
                  <span>{dailyMenuOpen ? '−' : '+'}</span>
                </button>
                {dailyMenuOpen && (
                  <div style={{paddingLeft: '10px'}}>
                    {canViewAccessCards && <NavLink to="/daily/access-cards" style={getSubNavLinkStyle}>门禁卡管理</NavLink>}
                    {canViewContributions && <NavLink to="/daily/contributions" style={getSubNavLinkStyle}>保险管理</NavLink>}
                    {canViewEmployeeInsurance && <NavLink to="/daily/employee-insurance" style={getSubNavLinkStyle}>员工保险</NavLink>}
                    {auth.hasPermission(['manage_archives','view_employees']) && <NavLink to="/daily/file-manager" style={getSubNavLinkStyle}>文件管理</NavLink>}
                    {(auth.hasPermission(['reimbursement_view']) || auth.hasPermission(['reimbursement_manage'])) && <NavLink to="/daily/reimbursements" style={getSubNavLinkStyle}>报销管理</NavLink>}

                    {canViewFeatureEntry && <NavLink to="/daily/special-monitoring" style={getSubNavLinkStyle}>功能区入口</NavLink>}

                    {(auth.hasPermission(['duty_task_manage']) || auth.hasPermission(['duty_task_record_manage']) || auth.hasPermission(['duty_task_view_all'])) && <NavLink to="/daily/duty-tasks" style={getSubNavLinkStyle}>履职任务</NavLink>}
                    {canViewWorkPlans && <NavLink to="/daily/work-plans" style={getSubNavLinkStyle}>工作计划</NavLink>}
                    {canViewWorkNotes && <NavLink to="/daily/work-notes" style={getSubNavLinkStyle}>工作笔记</NavLink>}

                    {canViewTempWorkers && <NavLink to="/daily/temp-worker-usage" style={getSubNavLinkStyle}>临时用工</NavLink>}
                    {canViewHrData && <NavLink to="/daily/hr-data" style={getSubNavLinkStyle}>人力数据</NavLink>}
                    {(auth.hasPermission(['dorm_view']) || auth.hasPermission(['dorm_manage'])) && <NavLink to="/daily/dorms" style={getSubNavLinkStyle}>宿舍管理</NavLink>}
                    {(auth.hasPermission(['employee_vehicles_view']) || auth.hasPermission(['employee_vehicles_manage']) || auth.hasPermission(['vehicle_violations_view_all']) || auth.hasPermission(['vehicle_violations_manage'])) && <NavLink to="/daily/employee-vehicles" style={getSubNavLinkStyle}>员工车辆</NavLink>}

                    {/* 请假加班模块导航 */}
                    {canViewLeaveOvertime && [
                      <NavLink key="leave-overtime" to="/daily/leave-overtime" style={getSubNavLinkStyle}>请假加班</NavLink>,
                      (auth.hasPermission(['leave_overtime_approval']) || auth.hasPermission(['leave_application_approve']) || auth.hasPermission(['overtime_application_approve'])) && <NavLink key="approval" to="/daily/leave-overtime/approval" style={getSubNavLinkStyle}>审批管理</NavLink>,
                      auth.hasPermission(['leave_overtime_data_analysis']) && <NavLink key="data-analysis" to="/daily/leave-overtime/data-analysis" style={getSubNavLinkStyle}>数据分析</NavLink>
                    ]}
                  </div>
                )}
              </>
            ) : null}

            {/* 行为管控分组 */}
            {([
              'behavior_tags_view',
              'behavior_tags_manage',
              'behavior_dashboard_view',
              'behavior_profile_view',
              'behavior_intervention_view',
              'behavior_intervention_manage',
              'behavior_daily_checkin_view',
              'behavior_daily_checkin_manage',
              'safety_points_view',
              'safety_points_manage'
            ].some(perm => auth.hasPermission([perm]))) && (
              <>
                <button onClick={() => setBehaviorMenuOpen(!behaviorMenuOpen)} style={{...navLinkStyle, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>行为管控</span>
                  <span>{behaviorMenuOpen ? '−' : '+'}</span>
                </button>
                {behaviorMenuOpen && (
                  <div style={{paddingLeft: '10px'}}>
                    {(auth.hasPermission(['safety_points_view']) || auth.hasPermission(['safety_points_manage'])) && (
                      <NavLink to="/daily/safety-points" style={getSubNavLinkStyle}>安全积分</NavLink>
                    )}
                    {([
                      'behavior_tags_view',
                      'behavior_tags_manage',
                      'behavior_dashboard_view',
                      'behavior_profile_view',
                      'behavior_intervention_view',
                      'behavior_intervention_manage'
                    ].some(perm => auth.hasPermission([perm]))) && (
                      <NavLink to="/daily/behavior" style={getSubNavLinkStyle}>行为管控</NavLink>
                    )}
                    {([
                      'behavior_daily_checkin_view',
                      'behavior_daily_checkin_manage',
                      'behavior_dashboard_view'
                    ].some(perm => auth.hasPermission([perm]))) && (
                      <NavLink to="/daily/behavior-daily-checkin" style={getSubNavLinkStyle}>日常打卡</NavLink>
                    )}
                  </div>
                )}
              </>
            )}
            
            {([
              auth.hasPermission(['manage_users']),
              auth.hasPermission(['manage_roles']),
              auth.hasPermission(['manage_settings']),
              auth.hasPermission(['manage_org_meta']),
              auth.hasPermission(['manage_api_test']),
              auth.hasPermission(['manage_api_permissions']),
              auth.hasPermission(['my_profile_view']),
              auth.hasPermission(['my_profile_manage']),
              auth.hasPermission(['onboarding_materials_view']),
              auth.hasPermission(['onboarding_materials_manage']),
              canViewFeedbackAdmin
            ].some(Boolean)) && (
              <>
                <button onClick={() => setManagementMenuOpen(!managementMenuOpen)} style={{...navLinkStyle, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>系统管理</span>
                  <span>{managementMenuOpen ? '−' : '+'}</span>
                </button>
                {managementMenuOpen && (
                  <div style={{paddingLeft: '10px'}}>
                    {auth.hasPermission(['manage_users']) && <NavLink to="/management/users" style={getSubNavLinkStyle}>用户管理</NavLink>}
                    {auth.hasPermission(['manage_roles']) && <NavLink to="/management/roles" style={getSubNavLinkStyle}>权限设置</NavLink>}
                    {auth.hasPermission(['manage_api_test']) && <NavLink to="/management/api-center" style={getSubNavLinkStyle}>API 中心</NavLink>}
                    {auth.hasPermission(['manage_api_permissions']) && <NavLink to="/management/api-permissions" style={getSubNavLinkStyle}>API 分组权限</NavLink>}
                    {(auth.hasPermission(['my_profile_view']) || auth.hasPermission(['my_profile_manage']) || auth.hasPermission(['manage_settings']) || auth.hasPermission(['manage_roles']) || auth.hasPermission(['manage_users'])) && <NavLink to="/management/my-profile" style={getSubNavLinkStyle}>我的资料</NavLink>}
                    {(auth.hasPermission(['onboarding_materials_view']) || auth.hasPermission(['onboarding_materials_manage']) || auth.hasPermission(['manage_settings']) || auth.hasPermission(['manage_roles'])) && <NavLink to="/management/onboarding-materials" style={getSubNavLinkStyle}>入职资料</NavLink>}
                    {(auth.hasPermission(['manage_settings']) || auth.hasPermission(['manage_org_meta'])) && <NavLink to="/settings" style={getSubNavLinkStyle}>系统设置</NavLink>}
                    {canViewFeedbackAdmin && <NavLink to="/management/feedback" style={getSubNavLinkStyle}>意见反馈</NavLink>}
                    <a href={pdfViewerEntryUrl} target="_blank" rel="noopener noreferrer" style={subNavLinkStyle}>PDF内嵌预览</a>
                  </div>
                )}
              </>
            )}
          </nav>
          <div>
            <button onClick={auth.logout} style={{ ...navLinkStyle, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', marginTop: 10, textAlign: 'center', width: '100%' }}>退出登录</button>
          </div>
        </div>
        {/* Right Content Area */}
        <div className="app-content">
          <div className="app-mobile-header">
            <button
              type="button"
              className="app-mobile-menu-button"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              aria-label={isSidebarOpen ? '关闭侧边栏' : '打开侧边栏'}
              aria-expanded={isSidebarOpen}
            >
              ☰
            </button>
            <div className="app-mobile-title">{systemName}</div>
          </div>
          <main className="app-main">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
};

export default Layout;

